import { decodeFunctionData, recoverAddress, type Hex } from "viem";
import { ELYSIUM, elysiumWriteBlock, isElysium, isElysiumFreePolicy, sameIdentity } from "../config/elysium";
import { ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import type { PreparedNftTransaction } from "./transaction-preflight";
import type { Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadOptions } from "./read-rpc";
import { readElysiumHead, checkElysiumContracts, confirmElysiumHead } from "./elysium-readiness";
import type { ContractRead } from "./quote-contract-reads";
import { observeRedeemDeployment } from "./redeem-fee-route";
import type { BootstrapResponse } from "../api/schemas";
import { responseNow } from "../api/response-time";

export function assertElysiumMintPermission(input: PreparedNftTransaction): BootstrapResponse {
  const bootstrap = input.mintBootstrap?.();
  if (!bootstrap || !isElysium(bootstrap)) throw new Error("ELYSIUM_MINT_REVIEW_REQUIRED");
  const reason = elysiumWriteBlock(bootstrap);
  if (reason) throw new Error(reason);
  return bootstrap;
}

/** Read-only signed-domain and implementation checks shared by paid, task and referral mint. */
export async function preflightElysiumMint(provider: Eip1193Provider, input: PreparedNftTransaction,
  assertCurrent: () => void, options: RpcReadOptions) {
  const bootstrap = assertElysiumMintPermission(input);
  const call = decodeFunctionData({ abi: ponsRewardNftAbi, data: input.data });
  if (call.functionName !== "mint") throw new Error("ELYSIUM_MINT_REVIEW_REQUIRED");
  const [authorization, signature] = call.args;
  const freeMint = Boolean(input.taskMint || input.referralMint);
  const policyMatches = freeMint ? isElysiumFreePolicy(authorization.rewardPolicyVersion, authorization.rewardPolicyHash) :
    sameIdentity(authorization.rewardPolicyHash, ELYSIUM.policyHash) && authorization.rewardPolicyVersion === BigInt(ELYSIUM.policyVersion);
  if (!sameIdentity(input.to, ELYSIUM.collection) || !sameIdentity(input.mintSigner, ELYSIUM.signer) ||
    !sameIdentity(authorization.sourceToken, ELYSIUM.token) || !sameIdentity(authorization.instanceId, ELYSIUM.instanceId) ||
    !policyMatches || freeMint && input.valueWei !== 0n ||
    !sameIdentity(authorization.wallet, input.account) || authorization.quotedMintFeeWei !== input.valueWei ||
    !/^0x[0-9a-f]{130}$/i.test(signature)) throw new Error("ELYSIUM_MINT_IDENTITY_MISMATCH");
  // Keep this observation on its BE response clock plus monotonic elapsed time.
  // Later polling must not replace its clock; a missing live HTTP Date grants nothing.
  const observation = await readElysiumHead(provider, assertCurrent, options, () => {
    const now = responseNow(bootstrap);
    if (now === null) throw new Error("MINT_SERVER_TIME_UNAVAILABLE");
    return now;
  });
  const pin = `0x${observation.blockNumber.toString(16)}` as Hex;
  // Share only code reads at this pinned block, within this operation. Never
  // cache latest-state reads or reuse this evidence after simulation.
  const codeReads = new Map<string, Promise<unknown>>();
  const pinnedProvider: Eip1193Provider = { request: args => {
    const params = args.params as readonly unknown[] | undefined;
    if (args.method !== "eth_getCode" || params?.[1] !== pin) return provider.request(args);
    const key = String(params[0]).toLowerCase();
    let pending = codeReads.get(key);
    if (!pending) { pending = provider.request(args); codeReads.set(key, pending); }
    return pending;
  } };
  const getters: ContractRead[] = [
    { to: input.to, abi: ponsRewardNftAbi, functionName: "mintAuthorizationDigest", args: [authorization] },
    ...["FEE_VAULT", "RANDOMNESS_ADAPTER", "INSTANCE_ID", "AUTHORIZATION_SIGNER"].map(functionName =>
      ({ to: input.to, abi: ponsRewardNftAbi, functionName })),
    ...(!freeMint ? [{ to: input.to, abi: ponsRewardNftAbi, functionName: "mintFeeWeiForTier",
      args: [authorization.selectedTierUsd] }] : []),
  ];
  const [deployment, values] = await Promise.all([
    observeRedeemDeployment(pinnedProvider, input.to, pin, assertCurrent, options),
    checkElysiumContracts(pinnedProvider, observation, assertCurrent, options, true, getters),
  ]);
  await confirmElysiumHead(provider, observation, assertCurrent, options);
  const [digest, vault, adapter, instance, signer, fee] = values;
  if (!sameIdentity(deployment.implementation, ELYSIUM.implementation) ||
    !sameIdentity(deployment.implementationCodeHash, ELYSIUM.implementationCodeHash) ||
    typeof vault !== "string" || !sameIdentity(vault, ELYSIUM.vault) ||
    typeof adapter !== "string" || !sameIdentity(adapter, ELYSIUM.readiness) ||
    typeof digest !== "string" || !/^0x[0-9a-f]{64}$/i.test(digest)) throw new Error("ELYSIUM_MINT_IDENTITY_MISMATCH");
  const recovered = await recoverAddress({ hash: digest as Hex, signature }).catch(() => null);
  assertCurrent();
  if (!sameIdentity(recovered, input.mintSigner!)) throw new Error("InvalidAuthorizationSignature: Elysium signer/domain mismatch");
  // Called after exact-call simulation; preserve the admitted proxy implementation/code.
  const recheck = async () => {
    assertCurrent();
    const [current, header] = await Promise.all([
      observeRedeemDeployment(provider, input.to, "latest", assertCurrent, options),
      readRpc(provider, { method: "eth_getBlockByNumber", params: [pin, false] }, options),
    ]);
    if (JSON.stringify(current) !== JSON.stringify(deployment)) throw new Error("Elysium mint implementation changed. Review again.");
    assertCurrent();
    if (!header || typeof header !== "object" || !sameIdentity((header as { hash?: string }).hash, observation.blockHash))
      throw new Error("ELYSIUM_HEAD_REORG");
  };
  return { recheck, code: await codeReads.get(input.to.toLowerCase()),
    block: { number: pin, hash: observation.blockHash, timestamp: observation.timestamp },
    mint: [freeMint ? 0n : fee, instance, signer] };
}
